/**
 * F-148 §10.2 header form: supplier combobox, optional shipment reference with the
 * "Leave blank" help, linked receipt (read-only chip) and note. After create, the display
 * reference with its origin badge is shown. Client validation is feedback only (§3.1 rules).
 */
import { useId } from 'react';
import { useTranslation } from '@dukanti/shared';
import {
  RECEIVING_LIMITS,
  validateShipmentReference,
  type ReceivingDraftHeaderInput,
  type ReceivingReferenceView,
  type ReceivingSupplier,
  type ReceivingSupplierInput,
} from '@dukanti/shared/admin';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { ReferenceBadge } from './reference-badge';
import { SupplierCombobox } from './supplier-combobox';

/** Header value while the supplier may still be unchosen (new-draft form). */
export type HeaderFormValue = Omit<ReceivingDraftHeaderInput, 'supplier'> & {
  supplier: ReceivingSupplierInput | null;
};

export interface DraftHeaderFormProps {
  readonly value: HeaderFormValue;
  readonly supplierName: string;
  readonly onChange: (patch: Partial<HeaderFormValue>, supplierName?: string) => void;
  readonly supplierQuery: string;
  readonly onSupplierQueryChange: (query: string) => void;
  readonly suppliers: readonly ReceivingSupplier[];
  readonly suppliersLoading?: boolean;
  /** Present once the draft exists; shows the display reference and origin badge. */
  readonly reference?: ReceivingReferenceView | null;
  /** Display reference of the linked receipt, when `value.linkedReceiptId` is set. */
  readonly linkedReceiptLabel?: string | null;
  readonly disabled?: boolean;
  /** Show the "choose a supplier" error (after a create attempt). */
  readonly showSupplierError?: boolean;
  readonly className?: string;
}

// eslint-disable-next-line react-refresh/only-export-components -- §3.1 client feedback shared with the new-draft route
export function referenceValidationMessageKey(
  value: string | null,
): 'admin.receiving.new.referenceTooLong' | 'admin.receiving.new.referenceControlCharacter' | null {
  const validation = validateShipmentReference(value);
  if (validation.ok) return null;
  return validation.reason === 'too_long'
    ? 'admin.receiving.new.referenceTooLong'
    : 'admin.receiving.new.referenceControlCharacter';
}

export function DraftHeaderForm({
  value,
  supplierName,
  onChange,
  supplierQuery,
  onSupplierQueryChange,
  suppliers,
  suppliersLoading = false,
  reference = null,
  linkedReceiptLabel = null,
  disabled = false,
  showSupplierError = false,
  className,
}: DraftHeaderFormProps): React.JSX.Element {
  const { t } = useTranslation();
  const id = useId();
  const referenceId = `${id}-reference`;
  const referenceHelpId = `${id}-reference-help`;
  const referenceErrorId = `${id}-reference-error`;
  const noteId = `${id}-note`;

  const referenceErrorKey = referenceValidationMessageKey(value.shipmentReference);
  const supplierError =
    showSupplierError && value.supplier === null ? t('admin.receiving.new.supplierRequired') : null;

  return (
    <div className={cn('space-y-4 text-start', className)} data-testid="draft-header-form">
      <SupplierCombobox
        value={value.supplier}
        displayName={supplierName}
        onChange={(supplier, name) => onChange({ supplier }, name)}
        query={supplierQuery}
        onQueryChange={onSupplierQueryChange}
        suppliers={suppliers}
        isLoading={suppliersLoading}
        disabled={disabled}
        error={supplierError}
      />

      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Label htmlFor={referenceId} className="text-sm">
            {t('admin.receiving.new.reference')}
          </Label>
          <span className="text-muted-foreground text-xs">
            {t('admin.receiving.new.referenceOptional')}
          </span>
        </div>
        <Input
          id={referenceId}
          value={value.shipmentReference ?? ''}
          onChange={(event) =>
            onChange({ shipmentReference: event.target.value === '' ? null : event.target.value })
          }
          disabled={disabled}
          autoComplete="off"
          maxLength={RECEIVING_LIMITS.maxReferenceLength * 2}
          aria-describedby={
            referenceErrorKey ? `${referenceHelpId} ${referenceErrorId}` : referenceHelpId
          }
          aria-invalid={referenceErrorKey ? true : undefined}
          className="min-h-11 text-start"
          data-testid="reference-input"
        />
        <p id={referenceHelpId} className="text-muted-foreground text-xs">
          {t('admin.receiving.new.referenceHelp')}
        </p>
        {referenceErrorKey ? (
          <p id={referenceErrorId} className="text-destructive text-xs" role="alert">
            {t(referenceErrorKey)}
          </p>
        ) : null}
        {reference ? <ReferenceBadge reference={reference} className="pt-1" /> : null}
      </div>

      {value.linkedReceiptId ? (
        <div className="space-y-1">
          <p className="text-sm font-medium">{t('admin.receiving.new.linkedReceipt')}</p>
          <p className="text-muted-foreground text-sm" data-testid="linked-receipt">
            <bdi dir="ltr">{linkedReceiptLabel ?? value.linkedReceiptId}</bdi>
          </p>
        </div>
      ) : null}

      <div className="space-y-1">
        <Label htmlFor={noteId} className="text-sm">
          {t('admin.receiving.new.note')}
        </Label>
        <Textarea
          id={noteId}
          value={value.note ?? ''}
          onChange={(event) =>
            onChange({ note: event.target.value === '' ? null : event.target.value })
          }
          disabled={disabled}
          maxLength={RECEIVING_LIMITS.maxNote}
          className="text-start"
          data-testid="header-note"
        />
      </div>
    </div>
  );
}
